/**
 * Public team browse + follow, and the invite-accept landing
 * (AC-FANATIC-002, AC-FANATIC-003).
 *
 * One component serves both because they are the same decision from the
 * spectator's point of view — "may I see this team's game?":
 *
 * | `?invite_token=` present | Renders                                    |
 * | ------------------------ | ------------------------------------------ |
 * | yes                      | invite preview + Accept                    |
 * | no                       | searchable directory + per-team Follow     |
 *
 * The follow affordance is driven entirely by the server's `follow_action` on
 * each row, so the client never re-derives access-mode rules:
 *
 * | `follow_action`  | Affordance                                          |
 * | ---------------- | --------------------------------------------------- |
 * | `follow`         | Follow button → `POST /fanatic/v1/grants`             |
 * | `accept_invite`  | "Use the link in your invite email"                 |
 * | `request_invite` | invite-only explainer (no self-service grant)        |
 * | `blocked`        | disabled — the team removed this account for good    |
 * | `none`           | already following → switch to it                    |
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { useApiClient } from '../../app/providers';
import { useToast } from '../../app/toast-context';
import type {
  FanaticAccessMode,
  FanaticInvitePreview,
  PublicTeam,
  PublicTeamSortField,
} from '../../lib/api-types';
import {
  FANATIC_INVITE_PARAM,
  FANATIC_TEAM_PARAM,
  FANATIC_VIEWS,
  ROUTES,
} from '../../lib/constants';

import { toFanaticMessage, useFanaticPortal } from './fanatic-portal';

const PAGE_SIZE = 12;
const SEARCH_DEBOUNCE_MS = 250;

const SORT_OPTIONS: ReadonlyArray<{ value: PublicTeamSortField; label: string }> = [
  { value: 'name', label: 'Team name' },
  { value: 'recent_game', label: 'Most recent game' },
  { value: 'follower_count', label: 'Followers' },
];

const ACCESS_MODE_LABEL: Record<FanaticAccessMode, string> = {
  'public-follow': 'Anyone can follow',
  'invite-only': 'Invite only',
};

/** URL parameters this page owns, so a refresh or a shared link restores state. */
const PARAM = {
  query: 'q',
  mode: 'mode',
  sort: 'sort',
  direction: 'dir',
  page: 'page',
} as const;

function readSort(raw: string | null): PublicTeamSortField {
  return SORT_OPTIONS.some((option) => option.value === raw)
    ? (raw as PublicTeamSortField)
    : 'name';
}

function readPage(raw: string | null): number {
  const parsed = Number.parseInt(raw ?? '1', 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 1;
}

export function FanaticBrowseFollowPage(): JSX.Element {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get(FANATIC_INVITE_PARAM);
  return inviteToken === null || inviteToken.trim() === '' ? (
    <TeamDirectory />
  ) : (
    <InviteAccept token={inviteToken} />
  );
}

// ------------------------------------------------------------------ directory

function TeamDirectory(): JSX.Element {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { reload, selectTeam, grants } = useFanaticPortal();
  const [searchParams, setSearchParams] = useSearchParams();

  const query = searchParams.get(PARAM.query) ?? '';
  const mode = searchParams.get(PARAM.mode);
  const sort = readSort(searchParams.get(PARAM.sort));
  const direction = searchParams.get(PARAM.direction) === 'desc' ? 'desc' : 'asc';
  const page = readPage(searchParams.get(PARAM.page));

  const [draftQuery, setDraftQuery] = useState(query);
  const [teams, setTeams] = useState<PublicTeam[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busySlug, setBusySlug] = useState<string | null>(null);
  const [requestedSlug, setRequestedSlug] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  const patchParams = useCallback(
    (changes: Record<string, string | null>) => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(changes)) {
        if (value === null || value === '') next.delete(key);
        else next.set(key, value);
      }
      setSearchParams(next);
    },
    [searchParams, setSearchParams]
  );

  // Debounce the search box into the URL: one source of truth, refresh-safe.
  useEffect(() => {
    if (draftQuery === query) return undefined;
    const timer = setTimeout(
      () => patchParams({ [PARAM.query]: draftQuery, [PARAM.page]: null }),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [draftQuery, patchParams, query]);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    void (async () => {
      try {
        const result = await apiClient.browsePublicTeams(
          {
            q: query === '' ? undefined : query,
            access_mode:
              mode === 'public-follow' || mode === 'invite-only'
                ? (mode as FanaticAccessMode)
                : undefined,
            sort,
            direction,
            page,
            page_size: PAGE_SIZE,
          },
          controller.signal
        );
        setTeams(result.teams);
        setTotal(result.pagination.total);
        setTotalPages(Math.max(1, result.pagination.total_pages));
        setError(null);
      } catch (cause) {
        if (controller.signal.aborted) return;
        setError(toFanaticMessage(cause, 'We could not load the team directory.'));
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    })();
    return () => controller.abort();
  }, [apiClient, direction, mode, page, query, reloadToken, sort]);

  const follow = async (team: PublicTeam): Promise<void> => {
    setBusySlug(team.slug);
    try {
      const grant = await apiClient.followTeam({ tenantId: team.tenant_id });
      showToast(`You now follow ${team.name}.`, 'success');
      reload();
      selectTeam(grant);
      navigate(
        `${ROUTES.fanaticPortal}?${FANATIC_TEAM_PARAM}=${encodeURIComponent(
          grant.team_slug ?? team.slug
        )}`
      );
    } catch (cause) {
      setError(toFanaticMessage(cause, `We could not follow ${team.name}.`));
      setReloadToken((token) => token + 1);
    } finally {
      setBusySlug(null);
    }
  };

  const followedGrant = useMemo(
    () => new Map(grants.map((grant) => [grant.tenant_id, grant])),
    [grants]
  );

  return (
    <section className="fan-panel" aria-label="Find teams to follow">
      <div className="fan-panel__head">
        <h2 className="fan-panel__title">Find a team</h2>
        <p className="fan-panel__hint">
          Follow a team to see its scores, team totals and — where the team’s video provider allows
          it — its live stream. Following never gives you player-level data.
        </p>
      </div>

      <div className="fan-toolbar">
        <div className="fan-toolbar__field fan-toolbar__grow">
          <label htmlFor="fanatic-team-search">Search by name or team code</label>
          <input
            id="fanatic-team-search"
            onChange={(event) => setDraftQuery(event.target.value)}
            placeholder="e.g. Westview or westview-eagles"
            type="search"
            value={draftQuery}
          />
        </div>
        <div className="fan-toolbar__field">
          <label htmlFor="fanatic-team-mode">Who can follow</label>
          <select
            id="fanatic-team-mode"
            onChange={(event) =>
              patchParams({ [PARAM.mode]: event.target.value, [PARAM.page]: null })
            }
            value={mode ?? ''}
          >
            <option value="">All teams</option>
            <option value="public-follow">Anyone can follow</option>
            <option value="invite-only">Invite only</option>
          </select>
        </div>
        <div className="fan-toolbar__field">
          <label htmlFor="fanatic-team-sort">Sort by</label>
          <select
            id="fanatic-team-sort"
            onChange={(event) =>
              patchParams({ [PARAM.sort]: event.target.value, [PARAM.page]: null })
            }
            value={sort}
          >
            {SORT_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="fan-toolbar__field">
          <label htmlFor="fanatic-team-dir">Order</label>
          <select
            id="fanatic-team-dir"
            onChange={(event) => patchParams({ [PARAM.direction]: event.target.value })}
            value={direction}
          >
            <option value="asc">Ascending</option>
            <option value="desc">Descending</option>
          </select>
        </div>
      </div>

      {error !== null ? (
        <p className="banner banner--error" role="alert">
          {error}{' '}
          <button
            className="btn btn--ghost btn--sm"
            onClick={() => setReloadToken((token) => token + 1)}
            type="button"
          >
            Try again
          </button>
        </p>
      ) : null}

      {isLoading ? <p className="fan-empty">Searching teams…</p> : null}

      {!isLoading && error === null && teams.length === 0 ? (
        <p className="fan-empty">
          {query === '' && mode === null
            ? 'No teams have opened themselves up to spectators yet.'
            : 'No team matches those filters. Try a different name or clear the filters.'}
        </p>
      ) : null}

      {teams.length > 0 ? (
        <div className="fan-grid">
          {teams.map((team) => (
            <article className="fan-team-card" key={team.tenant_id}>
              <h3 className="fan-team-card__name">{team.name}</h3>
              <p className="fan-team-card__meta">
                {team.slug} · {ACCESS_MODE_LABEL[team.fanatic_access_mode]}
              </p>
              <p className="fan-team-card__meta">
                {team.follower_count} follower{team.follower_count === 1 ? '' : 's'}
                {team.latest_game === null ? '' : ` · latest: ${team.latest_game}`}
              </p>
              <div className="fan-team-card__actions">
                <FollowAction
                  busySlug={busySlug}
                  onFollow={() => void follow(team)}
                  onRequest={() => setRequestedSlug(team.slug)}
                  onSwitch={() => {
                    const grant = followedGrant.get(team.tenant_id);
                    if (grant !== undefined) selectTeam(grant);
                    navigate(
                      `${ROUTES.fanaticPortal}?${FANATIC_TEAM_PARAM}=${encodeURIComponent(
                        team.slug
                      )}`
                    );
                  }}
                  team={team}
                />
              </div>
              {requestedSlug === team.slug ? (
                <p className="banner banner--warning" role="status">
                  {team.name} is invite only. Ask the team’s coaching staff to send an invite from
                  Team Admin → Fanatics; the emailed link brings you straight back here.
                </p>
              ) : null}
            </article>
          ))}
        </div>
      ) : null}

      <div className="fan-pager">
        <span className="fan-pager__status">
          {total === 0
            ? 'No teams'
            : `Page ${page} of ${totalPages} · ${total} team${total === 1 ? '' : 's'}`}
        </span>
        <span className="fan-panel__actions">
          <button
            className="btn btn--secondary btn--sm"
            disabled={page <= 1}
            onClick={() => patchParams({ [PARAM.page]: String(page - 1) })}
            type="button"
          >
            Previous
          </button>
          <button
            className="btn btn--secondary btn--sm"
            disabled={page >= totalPages}
            onClick={() => patchParams({ [PARAM.page]: String(page + 1) })}
            type="button"
          >
            Next
          </button>
        </span>
      </div>
    </section>
  );
}

function FollowAction({
  team,
  busySlug,
  onFollow,
  onRequest,
  onSwitch,
}: {
  team: PublicTeam;
  busySlug: string | null;
  onFollow: () => void;
  onRequest: () => void;
  onSwitch: () => void;
}): JSX.Element {
  const isBusy = busySlug === team.slug;
  switch (team.follow_action) {
    case 'follow':
      return (
        <button
          className="btn btn--primary btn--sm"
          disabled={isBusy}
          onClick={onFollow}
          type="button"
        >
          {isBusy ? 'Following…' : `Follow ${team.name}`}
        </button>
      );
    case 'accept_invite':
      return (
        <span className="fan-badge fan-badge--pending">
          Invite pending — use the link in your email
        </span>
      );
    case 'request_invite':
      return (
        <button className="btn btn--secondary btn--sm" onClick={onRequest} type="button">
          How do I join?
        </button>
      );
    case 'blocked':
      return <span className="fan-badge fan-badge--revoked">This team removed your access</span>;
    default:
      return (
        <button className="btn btn--secondary btn--sm" onClick={onSwitch} type="button">
          Already following — view team
        </button>
      );
  }
}

// -------------------------------------------------------------- invite accept

function InviteAccept({ token }: { token: string }): JSX.Element {
  const apiClient = useApiClient();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { reload, selectTeam } = useFanaticPortal();
  const [preview, setPreview] = useState<FanaticInvitePreview | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    void (async () => {
      try {
        setPreview(await apiClient.previewFanaticInvite(token, controller.signal));
        setError(null);
      } catch (cause) {
        if (controller.signal.aborted) return;
        setError(toFanaticMessage(cause, 'Invite not found or expired. Please contact the team.'));
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    })();
    return () => controller.abort();
  }, [apiClient, token]);

  const accept = async (): Promise<void> => {
    setIsAccepting(true);
    try {
      const accepted = await apiClient.acceptFanaticInvite(token);
      showToast(`You now follow ${accepted.team_name ?? 'this team'}.`, 'success');
      reload();
      selectTeam({
        grant_id: accepted.grant_id,
        tenant_id: accepted.tenant_id,
        team_name: accepted.team_name,
        team_slug: accepted.team_slug,
        fanatic_access_mode: null,
        status: accepted.status,
        granted_at: accepted.accepted_at,
        granted_by: null,
        accepted_at: accepted.accepted_at,
        revoked_at: null,
        revoked_reason: null,
        is_revocation_irreversible: false,
        invite_expires_at: null,
        created_at: accepted.accepted_at ?? new Date().toISOString(),
        updated_at: accepted.accepted_at ?? new Date().toISOString(),
      });
      navigate(
        accepted.team_slug === null
          ? ROUTES.fanaticPortal
          : `${ROUTES.fanaticPortal}?${FANATIC_TEAM_PARAM}=${encodeURIComponent(
              accepted.team_slug
            )}`,
        { replace: true }
      );
    } catch (cause) {
      setError(toFanaticMessage(cause, 'We could not accept this invite.'));
    } finally {
      setIsAccepting(false);
    }
  };

  if (isLoading) return <p className="fan-empty">Checking your invite…</p>;

  if (error !== null || preview === null) {
    return (
      <section className="fan-panel" aria-label="Team invite">
        <h2 className="fan-panel__title">This invite cannot be used</h2>
        <p className="banner banner--error" role="alert">
          {error ?? 'Invite not found or expired. Please contact the team.'}
        </p>
        <div className="fan-panel__actions">
          <Link className="btn btn--primary" to={`${ROUTES.fanaticPortal}/${FANATIC_VIEWS.browse}`}>
            Browse teams instead
          </Link>
        </div>
      </section>
    );
  }

  const teamName = preview.team_name ?? preview.team_slug ?? 'this team';

  return (
    <section className="fan-panel" aria-label="Team invite">
      <div className="fan-panel__head">
        <h2 className="fan-panel__title">{teamName} invited you to follow along</h2>
      </div>
      <p className="fan-panel__hint">
        The invite was sent to <strong>{preview.invite_email}</strong>. Accepting gives you scores,
        team totals and — where the team’s video provider allows it — live video. It never gives you
        player-level data.
      </p>
      {preview.is_expired ? (
        <p className="banner banner--warning" role="status">
          This invite expired on {new Date(preview.invite_expires_at).toLocaleDateString()}. Ask the
          team to send a new one.
        </p>
      ) : (
        <p className="fan-panel__hint">
          Expires {new Date(preview.invite_expires_at).toLocaleDateString()}.
        </p>
      )}
      <div className="fan-panel__actions">
        <button
          className="btn btn--primary"
          disabled={isAccepting || preview.is_expired}
          onClick={() => void accept()}
          type="button"
        >
          {isAccepting ? 'Accepting…' : `Accept and follow ${teamName}`}
        </button>
        <Link className="btn btn--ghost" to={`${ROUTES.fanaticPortal}/${FANATIC_VIEWS.browse}`}>
          Not now
        </Link>
      </div>
    </section>
  );
}
